import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_BOOTSTRAP_LISTENER, APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { ApolloModule } from 'apollo-angular';
import { HttpLinkModule } from 'apollo-angular-link-http';
import { configuration } from 'src/app/app.config';
import { routes } from 'src/app/app.routes';
import { DashboardsModule } from 'src/app/dashboards/dashboards.module';
import { DutyLogsModule } from 'src/app/duty-logs/duty-logs.module';
import { FlightsModule } from 'src/app/flight-logs/flight-logs.module';
import { LoginModule } from 'src/app/login/login.module';
import { MaintenanceModule } from 'src/app/maintenance/maintenance.module';
import { PagesModule } from 'src/app/pages/pages.module';
import { ReportsModule } from 'src/app/reports/reports.module';
import { AUTH_CONFIG } from 'src/app/shared/authentication/auth.config.token';
import { FLIGHT_LOG_API_CONFIG } from 'src/app/shared/flight-log-api/flight-log-api-configuration';
import { SharedModule } from 'src/app/shared/shared.module';
import { IdentityModule } from 'src/lib/fl-identity/fl-identity.module';
import { oktaConfigFactory } from 'src/lib/fl-identity/fl-okta-config.provider';
import { AuthStoreEffects } from 'src/store/auth/auth-store.effects';
import { AppEffects } from '../../effects/app.effects';
import { DutyLogEffects } from '../../effects/duty-log.effects';
import { FlightLogEffects } from '../../effects/flight-log.effects';
import { IdentityEffects } from '../../effects/identity.effects';
import { metaReducers, reducers } from '../../store';
import { PilotsModule } from '../pilots/pilots.module';
import { TransmissionRunsModule } from '../transmission-runs/transmission-runs.module';
import { UserActivityReportsModule } from '../user-activity-reports/user-activity-reports.module';
import { AppBootstrapService, bootstrapApp } from './app-bootstrap.service';
import { AppInitService, initApp } from './app-init.service';
import { AuthenticationInterceptor } from './authentication.interceptor';
import { ConfigurationService } from './configuration.service';
import { ApiHeaderInterceptor } from './http/api-header-interceptor';
import { DateConvertorInterceptor } from './http/date-converter-interceptor';
import { ImpersonationInterceptor } from './impersonation.interceptor';
import { CurrentUser, provideUser, USER } from './user.service';
import { PrdPermissionsModule } from '../prd-permissions/prd-permissions.module';

// Section (Feature Modules) - TODO: lazy load
// if lazy loading reducers is required at future point:
// https://github.com/ngrx/store/issues/197
// StoreModule.provideStore(rootReducer),
// Shared modules
// TODO below modules should not be exported?
@NgModule({
  imports: [
    BrowserModule,
    FlightsModule,
    PagesModule,
    DashboardsModule,
    DutyLogsModule,
    ReportsModule,
    MaintenanceModule,
    LoginModule,
    HttpLinkModule,
    ApolloModule,
    MatNativeDateModule,
    RouterModule.forRoot(routes),
    EffectsModule.forRoot([
      IdentityEffects,
      AppEffects,
      DutyLogEffects,
      FlightLogEffects,
      AuthStoreEffects,
    ]),
    SharedModule.forRoot(configuration),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        // Eventually it would be nice to set this to true
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreDevtoolsModule.instrument(),
    // Okta
    OktaAuthModule,
    IdentityModule,
    PilotsModule,
    TransmissionRunsModule,
    UserActivityReportsModule,
    PrdPermissionsModule,
  ],
  declarations: [],
  providers: [
    [
      {
        provide: APP_INITIALIZER,
        useFactory: initApp,
        deps: [AppInitService, Injector],
        multi: true,
      },

      {
        provide: APP_BOOTSTRAP_LISTENER,
        useFactory: bootstrapApp,
        deps: [AppBootstrapService],
        multi: true,
      },
      { provide: AUTH_CONFIG, useValue: configuration.auth },
      {
        provide: USER,
        useFactory: provideUser,
        deps: [CurrentUser],
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: DateConvertorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiHeaderInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthenticationInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ImpersonationInterceptor,
        multi: true,
      },
      { provide: FLIGHT_LOG_API_CONFIG, useValue: configuration.flightLog },
      {
        provide: OKTA_CONFIG,
        useFactory: oktaConfigFactory,
        deps: [ConfigurationService],
      },
    ],
  ],

  exports: [SharedModule, PagesModule, LoginModule, FlightsModule, ApolloModule],
})
export class CoreModule {}
